import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Button as BsButton } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { toast } from "react-toastify";
import { createApiService } from "./shared/apiConfig";
import {
  ContractsRuleApi,
  SubscriptionGroupsApi,
} from "./shared/api/axios-client";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { Folder2Open, FolderPlus, Pencil, Trash } from "react-bootstrap-icons";
import {
  InviteParkerForm,
  InviteParkerModal,
} from "./UserPlanDetailsPageInviteParkerModal";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { SubListEntry, Vehicle } from "./Data/ApiTypes";
import ParkerEditModal from "./ParkerEditModal";
import { AddVehicleModal } from "./Controls/AddVehicleModal";
import { ChooseVehicleModal } from "./Controls/ChooseVehicleModal";
import { showMoney } from "./Data/Money";
import {
  usePendingChanges,
  useUnsavedChangesGuard,
} from "./PendingChangesContext";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { parseParkerInfo } from "./Data/ApiParse";
import { PageTitle } from "./Controls/PageTitle";
import { GroupModal } from "./GroupModal";
import { ActionDropdown } from "./Controls/ActionDropdown";
import { ChangeParkerGroupModal } from "./ChangeParkerGroupModal";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { useSubscriptionVehiclesAndIntegrations } from "./useSubscriptionVehiclesAndIntegrations";
import { ContractRuleModal, ContractRuleForm } from "./ContractRuleModal";

enum Context {
  FromGroup,
  FromPlanGroups,
}

export const UNASSIGNED_GROUP_ID = 0;

export function SubscriptionManageGroupParkersPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const [subscription, loadedGroups] = useLoaderData() as any;

  const groupId = searchParams.get("groupId")
    ? Number(searchParams.get("groupId"))
    : null;

  const context: Context = groupId ? Context.FromGroup : Context.FromPlanGroups;

  const [committedParkers, setCommittedParkers] = useState<any[]>([]);

  const [allGuestParkers, setAllGuestParkers] = useState<any[]>([]);

  const [groupGuestParkers, setGroupGuestParkers] = useState<any[]>([]);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(0);

  const [search, setSearch] = useState("");
  const [showAddParker, setShowAddParker] = useState(false);

  const [showRemove, setShowRemove] = useState(false);
  const [removeParkerId, setRemoveParkerId] = useState<number | null>(null);
  const [removeParkerFromGroup, setRemoveParkerFromGroup] = useState(false);
  const [showChangeGroup, setShowChangeGroup] = useState(false);
  const [selectedParker, setSelectedParker] = useState<any | null>(null);
  const [showEditParkerModal, setShowEditParkerModal] = useState(false);
  const [chooseModalVehicles, setChooseModalVehicles] = useState<Vehicle[]>([]);
  const [chooseModalExclude, setChooseModalExclude] = useState<number[]>([]);
  const [showChooseVehicle, setShowChooseVehicle] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groups, setGroups] = useState(loadedGroups);
  const [showAddContract, setShowAddContract] = useState(false);
  const { hasChanges, setHasChanges, setHandlers } = usePendingChanges();
  const { showModal, stay, discard } = useUnsavedChangesGuard(
    hasChanges,
    () => {
      setAllGuestParkers(committedParkers);
      setHasChanges(false);
    }
  );
  const subGroupsApi = createApiService(SubscriptionGroupsApi);
  const contractsApi = createApiService(ContractsRuleApi);

  const { vehicleAdd, vehicleLink } = useSubscriptionVehiclesAndIntegrations({
    facilityId: subscription.facility.facilityId,
    parkerId: subscription.parkerId,
    subscriptionId: subscription.subscriptionId,
    vehicles: subscription.vehicles,
    setChooseModalVehicles,
    setChooseModalExclude,
    setShowChoose: setShowChooseVehicle,
    setParker: setSelectedParker,
    setParkers: setAllGuestParkers,
  });

  useEffect(() => {
    if (!subscription?.parkers) return;

    const guests = subscription.parkers.map((guest: any) => ({
      subscriptionId: subscription.subscriptionId,
      sub: subscription,
      parkerId: guest.parker.parkerId,
      name: guest.parker.name,
      email: guest.parker.emailAddress,
      plan: subscription.plan.name,
      planId: subscription.plan.planId,
      isManaged: guest.parker.isManaged,
      quantity: guest.quantity,
      parker: guest.parker,
      vehicles: guest.vehicles,
      facilityId: subscription.plan.facilityId,
      groupId: guest.subscriptionGroupId,
      groupName: guest.subscriptionGroupName,
    }));

    setCommittedParkers(guests);
    setAllGuestParkers(guests);
  }, [subscription]);

  useEffect(() => {
    if (
      context === Context.FromPlanGroups &&
      groups.length > 0 &&
      selectedGroupId === 0
    ) {
      setSelectedGroupId(0);
    } else if (context === Context.FromGroup) {
      setSelectedGroupId(groupId!);
    }
  }, [groups, context, selectedGroupId]);

  useEffect(() => {
    let guests = [...allGuestParkers];

    if (context === Context.FromGroup && selectedGroupId) {
      guests = guests.filter((p) => p.isDraft || p.groupId === selectedGroupId);
    }

    if (context === Context.FromPlanGroups && selectedGroupId !== 0) {
      guests = guests.filter((p) => p.isDraft || p.groupId === selectedGroupId);
    }

    if (search) {
      const searchTerm = search.toLowerCase();
      guests = guests.filter((p) => {
        const lpnText = getLpnText(p.vehicles).toLowerCase();
        return `${p.name} ${lpnText}`.toLowerCase().includes(searchTerm);
      });
    }

    setGroupGuestParkers(guests);
  }, [allGuestParkers, selectedGroupId, search, context]);

  const hasRealChanges = useMemo(() => {
    if (allGuestParkers.some((p) => p.isDraft)) return true;

    return allGuestParkers.some((p) => {
      const committed = committedParkers.find((c) => c.parkerId === p.parkerId);
      return committed && committed.groupId !== p.groupId;
    });
  }, [allGuestParkers, committedParkers]);

  useEffect(() => {
    setHasChanges(hasRealChanges);
  }, [hasRealChanges, setHasChanges]);

  const isEditingContract =
    groups.find((x) => x.id == groupId)?.contractRules?.length > 0;

  const modalGroups = groups
    .filter((x) => x.contractRules?.length === 0 || !x.contractRules)
    .map((g) => ({
      id: g.id!,
      name: g.name!,
    }));

  type ParkerRow = {
    parkerId: number;
    draftId?: string;
    isDraft: boolean;

    name: string;
    email: string;
    quantity: number;
    isManaged: boolean;

    parker: any;
    vehicles: Vehicle[];

    groupId: number | null;
    groupName: string | null;

    subscriptionId: number;
    lpn: string;
    plan: string;

    sub: {
      subscriptionId: number;
      status: SubscriptionStatus;
    };
  };

  const saveChanges = useCallback(async () => {
    const api = createApiService(SubscriptionGroupsApi);

    const groupChanges = allGuestParkers.filter(
      (p) =>
        !p.isDraft &&
        committedParkers.find((c) => c.parkerId === p.parkerId)?.groupId !==
          p.groupId
    );

    for (const p of groupChanges) {
      await api.changeParkerGroup({
        serverRequestsParkerGroupChangeRequest: {
          parkerId: p.parkerId!,
          subscriptionGroupId: p.groupId,
          subscriptionId: subscription.subscriptionId,
        },
      });
    }

    const drafts = allGuestParkers.filter((p) => p.isDraft);
    const created: ParkerRow[] = [];

    for (const d of drafts) {
      const resp = await Api.subParkerInvite(
        subscription.facility.facilityId,
        subscription.subscriptionId,
        {
          name: d.name,
          emailAddress: d.email,
          subscriptionGroupId: d.groupId,
          quantity: 1,
          isLoginEnabled: false,
          subscriptionId: subscription.subscriptionId,
        }
      );

      processResult(resp, (val) => {
        created.push({
          parkerId: val.parker.parkerId,
          isDraft: false,

          name: val.parker.name,
          email: val.parker.emailAddress,
          quantity: val.quantity,
          isManaged: val.parker.isManaged,

          parker: parseParkerInfo(val.parker),
          vehicles: [],

          groupId: d.groupId,
          groupName:
            d.groupId != null
              ? groups!.find((g) => g.id === d.groupId)?.name ?? null
              : null,

          subscriptionId: 0,
          lpn: "N/A",
          plan: "",

          sub: {
            subscriptionId: 0,
            status: SubscriptionStatus.Active,
          },
        });
      });
    }

    const committed = [
      ...allGuestParkers.filter((p) => !p.isDraft),
      ...created,
    ];

    setAllGuestParkers(committed);
    setCommittedParkers(committed);
    setHasChanges(false);

    toast.success("Changes saved successfully");
  }, [
    allGuestParkers,
    committedParkers,
    subscription.subscriptionId,
    setHasChanges,
  ]);

  const cancelChanges = useCallback(() => {
    setAllGuestParkers([...committedParkers]);
    setSelectedParker(null);
    setShowCancelConfirm(false);
    setHasChanges(false);
  }, [committedParkers]);

  useEffect(() => {
    setHandlers({
      onSave: saveChanges,
      onDiscard: () => setShowCancelConfirm(true),
    });
  }, [setHandlers, saveChanges, cancelChanges]);

  const getLpnText = (vehicles: Vehicle[]) =>
    vehicles?.length
      ? vehicles.map((v) => v.licensePlateNumber).join(" | ")
      : "N/A";

  const getPendingRowStyle = (isDraft: boolean, defaultColor?: string) => ({
    backgroundColor: isDraft ? "rgb(255 248 248)" : defaultColor,
  });

  async function assignParkerToGroup(
    parker: { parkerId?: number; draftId?: string; isDraft: boolean },
    groupId: number | null
  ) {
    setAllGuestParkers((prev) =>
      prev.map((p) => {
        const isSame =
          p.isDraft && parker.isDraft
            ? p.draftId === parker.draftId
            : !p.isDraft && !parker.isDraft
            ? p.parkerId === parker.parkerId
            : false;

        if (!isSame) return p;

        return {
          ...p,
          groupId,
          groupName:
            groupId == null
              ? null
              : groups.find((g) => g.id === groupId)?.name ?? null,
        };
      })
    );
  }

  async function createGroup(name: string) {
    const res = await subGroupsApi.addGroup({
      serverRequestsSubscriptionGroupAddRequest: {
        subscriptionId: subscription.subscriptionId,
        name,
      },
    });

    const group = res.data;

    if (group?.error) {
      toast.error(group.error);
      return;
    }

    setGroups((prev) => [
      ...prev,
      {
        id: group.id,
        name: group.name,
        parkerCount: 0,
      },
    ]);

    toast.success("Group created successfully.");

    return group;
  }

  const totalSpaces = subscription.quantity;
  const totalParkers = allGuestParkers.length;

  const unallocatedSpaces = totalSpaces - totalParkers;

  const unassignedCount = useMemo(
    () => allGuestParkers.filter((p) => !p.groupId).length,
    [allGuestParkers]
  );

  const pendingCount = useMemo(
    () => allGuestParkers.filter((p) => p.isDraft).length,
    [allGuestParkers]
  );

  const hasUnassigned =
    context === Context.FromPlanGroups && unassignedCount > 0;
  const hasUnallocated =
    context === Context.FromPlanGroups && unallocatedSpaces > 0;

  const showWarning = hasUnassigned || hasUnallocated;

  const currentGroupName =
    context === Context.FromGroup && groupId
      ? groups.find((g) => g.id === groupId)?.name
      : null;

  async function parkerInvite(
    data: InviteParkerForm,
    groupId?: number | null,
    plan?: SubListEntry,
    shouldCommit?: boolean
  ) {
    const commit = shouldCommit ?? true;

    if (!commit) {
      setAllGuestParkers((prev) => [
        ...prev,
        {
          draftId: crypto.randomUUID(),
          isDraft: true,
          name: data.name,
          email: data.emailAddress,
          groupId,
          vehicles: [],
          groupName:
            groupId != null
              ? groups!.find((g) => g.id === groupId)?.name ?? null
              : null,
        },
      ]);

      return;
    }

    const subscriptionGroupId = groupId ?? null;

    const populatedSub = subscription.subscriptioId || plan!.subscriptionId;

    const resp = await Api.subParkerInvite(
      subscription.facility.facilityId,
      populatedSub,
      {
        emailAddress: data.emailAddress,
        name: data.name,
        quantity: 1,
        subscriptionId: populatedSub,
        isLoginEnabled: data.isLoginEnabled,
        subscriptionGroupId,
      }
    );

    processResult(
      resp,
      (val) => {
        toast.success("Parker Added Successfully");

        setAllGuestParkers((prev) => [
          ...prev,
          {
            populatedSub,
            parkerId: val.parker.parkerId,
            name: val.parker.name,
            email: val.parker.emailAddress,
            quantity: val.quantity,
            isManaged: val.parker.isManaged,
            parker: parseParkerInfo(val.parker),
            vehicles: [],
            groupId: subscriptionGroupId,
            groupName:
              subscriptionGroupId != null
                ? groups!.find((g) => g.id === subscriptionGroupId)?.name ??
                  null
                : null,
            subscriptionId: populatedSub,
            lpn: "N/A",
            plan: plan?.plan.name || "",
            sub: {
              subscriptionId: populatedSub,
              status: SubscriptionStatus.Active,
            },
            isDraft: false,
          },
        ]);
      },
      (err) => {
        toast.error(err);
      }
    );
  }

  async function parkerRemove(parkerId: number, subId?: number) {
    await Api.subParkerRemove(
      subscription.facility.facilityId,
      subscription.subscriptionId || subId!,
      { parkerId }
    ).then((resp) =>
      processResult(
        resp,
        () => {
          setAllGuestParkers((prev) =>
            prev.filter((p) => p.parkerId !== parkerId)
          );
        },
        (err) => toast.error(err)
      )
    );
  }

  async function saveContract(data: {
    groupId: number;
    rules: ContractRuleForm[];
  }) {
    // REMOVE UI-ONLY FIELDS
    const cleanRules = (data.rules as any[]).map(
      ({ _priceText, _isPriceFocused, id, ...rule }) => rule
    );

    const res = await contractsApi.contractsRulePost({
      serverRequestsContractsRuleSaveRequest: {
        subscriptionGroupId: data.groupId,
        rules: cleanRules,
      },
    });

    const result = res.data;

    if (result?.error) {
      toast.error(result.error);
      return;
    }
    setGroups((prev) =>
      prev.map((g) =>
        g.id === data.groupId
          ? {
              ...g,
              contractRules: result.rules ?? [],
            }
          : g
      )
    );

    toast.success("Contract created successfully.");
    return result;
  }

  async function removeContract(groupId: number) {
    try {
      const res = await contractsApi.contractsRuleSubscriptionGroupIdDelete({
        subscriptionGroupId: groupId,
      });

      const result = res.data;

      if (result?.error) {
        toast.error(result.error);
        return;
      }

      setGroups((prev) =>
        prev.map((g) =>
          g.id === groupId
            ? {
                ...g,
                contractRules: [],
                hasContract: false,
              }
            : g
        )
      );

      toast.success("Contract removed successfully.");
    } catch (err) {
      toast.error("Failed to remove contract.");
    }
  }

  return (
    <div className="container-fluid py-3 d-flex flex-column h-100">
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <BsButton
              variant="link"
              className="p-0 mb-2 text-decoration-none text-danger"
              onClick={() => nav(-1)}
            >
              ← Back to Plan
            </BsButton>

            <PageTitle>Parkers</PageTitle>

            <div className="d-flex gap-2 fs-5 fw-semibold text-muted align-items-center">
              {currentGroupName && (
                <>
                  <Badge bg="secondary">{currentGroupName}</Badge>
                </>
              )}

              <span>{subscription.plan.name}</span>
            </div>
          </div>
        </div>
      </div>

      {showWarning && (
        <div className="alert alert-warning d-flex align-items-start gap-3 py-3 mb-3">
          <div className="fs-5 lh-1">⚠️</div>

          <div>
            <div className="fw-semibold mb-1">Attention Required</div>

            <div className="small mb-2">
              The following will be charged the base rate of{" "}
              <strong>{showMoney(subscription.price)}</strong>:
            </div>

            <ul className="mb-0 ps-3 small">
              {hasUnassigned && (
                <li>
                  <strong>{unassignedCount}</strong> parker
                  {unassignedCount > 1 ? "s are" : " is"} not assigned to a
                  group
                </li>
              )}

              {hasUnallocated && (
                <li>
                  <strong>{unallocatedSpaces}</strong> unallocated space
                  {unallocatedSpaces > 1 ? "s" : ""}
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      <div className="card mb-1">
        <div className="card-body py-4">
          <div className="row g-4 align-items-end">
            {context === Context.FromPlanGroups && (
              <div className="col-md-4">
                <label className="form-label small text-muted">
                  Filter by Group
                </label>
                <select
                  className="form-select"
                  value={selectedGroupId!}
                  onChange={(e) => {
                    const raw = Number(e.target.value);

                    if (raw === -1) {
                      setSelectedGroupId(null);
                    } else {
                      setSelectedGroupId(raw);
                    }
                  }}
                >
                  <option value={0}>All Groups</option>
                  <option value={-1}>Unassigned</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="col-md-12">
              <input
                className="form-control"
                placeholder="Search by name or LPN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className="card border-0 shadow-none flex-grow-1 overflow-auto "
        style={{
          paddingBottom: hasChanges ? 60 : 0,
        }}
      >
        <div className="card-header border-0 bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {groupGuestParkers.length} Parker
              {groupGuestParkers.length !== 1 ? "s" : ""}
            </h5>

            <div className="d-flex gap-2 align-items-center">
              {modalGroups.length > 0 && (
                <Button
                  size="sm"
                  className="btn-main"
                  onClick={() => {
                    if (groupId) setSelectedGroupId(groupId);
                    setShowAddContract(true);
                  }}
                >
                  {isEditingContract ? "Edit Contract" : "Add Contract"}
                </Button>
              )}
              <Button
                size="sm"
                className="btn-main"
                onClick={() => setShowAddParker(true)}
              >
                Add Parker
              </Button>

              {subscription.status === SubscriptionStatus.Active &&
                !groupId && (
                  <Button
                    size="sm"
                    className="btn-main "
                    onClick={() => setShowGroupModal(true)}
                  >
                    Add Group
                  </Button>
                )}
            </div>
          </div>

          {pendingCount > 0 && (
            <div style={{ marginTop: "-6px" }} className="small text-muted">
              * {pendingCount} pending save
            </div>
          )}
        </div>

        <div className="card d-none d-md-block">
          <div className="table-responsive">
            <table className="table mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Group</th>
                  <th>LPN</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupGuestParkers.map((p) => (
                  <tr key={p.parkerId || p.draftId}>
                    <td
                      className="fw-semibold text-primary py-3"
                      style={getPendingRowStyle(p.isDraft)}
                    >
                      {p.name}
                    </td>
                    <td className="py-3" style={getPendingRowStyle(p.isDraft)}>
                      {p.groupName ? (
                        <span className="badge rounded-pill bg-danger-subtle text-danger px-3 py-2">
                          {p.groupName}
                        </span>
                      ) : (
                        <span className="badge rounded-pill bg-light text-muted px-3 py-2">
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td style={getPendingRowStyle(p.isDraft)}>
                      {getLpnText(p.vehicles)}
                    </td>

                    <td
                      className="text-end py-3 pe-4"
                      style={getPendingRowStyle(p.isDraft)}
                    >
                      <div className="d-inline-flex gap-2">
                        <ActionDropdown
                          actions={[
                            {
                              label: p.groupId
                                ? "Change Group"
                                : "Assign to Group",
                              icon: p.groupId ? (
                                <Folder2Open
                                  size={16}
                                  className="text-muted mb-1"
                                />
                              ) : (
                                <FolderPlus
                                  size={16}
                                  className="text-muted mb-1"
                                />
                              ),
                              onClick: () => {
                                setSelectedParker(p);
                                setShowChangeGroup(true);
                              },
                            },
                            ...(!p.isDraft
                              ? [
                                  {
                                    label: "Edit Parker",
                                    icon: (
                                      <Pencil
                                        size={16}
                                        className="text-muted cursor-pointer mb-1"
                                      />
                                    ),
                                    onClick: () => {
                                      setSelectedParker(p);
                                      setShowEditParkerModal(true);
                                    },
                                  },
                                ]
                              : []),
                            {
                              label: p.groupId
                                ? "Remove from Group"
                                : "Remove Parker",
                              icon: (
                                <Trash size={16} className="text-muted mb-1" />
                              ),
                              onClick: () => {
                                if (p.isDraft && p.draftId) {
                                  if (p.groupId) {
                                    setAllGuestParkers((prev) =>
                                      prev.map((x) =>
                                        x.draftId === p.draftId
                                          ? {
                                              ...x,
                                              groupId: null,
                                              groupName: null,
                                            }
                                          : x
                                      )
                                    );
                                    return;
                                  }

                                  setAllGuestParkers((prev) =>
                                    prev.filter((x) => x.draftId !== p.draftId)
                                  );
                                  return;
                                }

                                setRemoveParkerId(p.parkerId);
                                p.groupId
                                  ? setRemoveParkerFromGroup(true)
                                  : setShowRemove(true);
                              },
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div
          className="d-block d-md-none px-3 mt-2"
          style={{ flex: 1, overflowY: "auto" }}
        >
          {groupGuestParkers.length === 0 ? (
            <div className="text-center text-muted py-3">
              No Parkers to Show
            </div>
          ) : (
            groupGuestParkers.map((p) => (
              <div
                key={p.parkerId || p.draftId}
                className="card mb-3 rounded-4 border-1"
                style={getPendingRowStyle(p.isDraft, "white")}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <h6 className="mb-1 fw-bold text-primary">{p.name}</h6>

                      {p.groupName ? (
                        <span className="badge rounded-pill bg-danger-subtle text-danger">
                          {p.groupName}
                        </span>
                      ) : (
                        <span className="badge rounded-pill bg-light text-muted">
                          Unassigned
                        </span>
                      )}
                    </div>
                    <ActionDropdown
                      actions={[
                        {
                          label: p.groupId ? "Change Group" : "Assign to Group",
                          icon: p.groupId ? (
                            <Folder2Open
                              size={18}
                              className="text-muted mb-1"
                            />
                          ) : (
                            <FolderPlus size={18} className="text-muted mb-1" />
                          ),
                          onClick: () => {
                            setSelectedParker(p);
                            setShowChangeGroup(true);
                          },
                        },
                        ...(!p.isDraft
                          ? [
                              {
                                label: "Edit Parker",
                                icon: (
                                  <Pencil
                                    size={16}
                                    className="text-muted cursor-pointer mb-1"
                                  />
                                ),
                                onClick: () => {
                                  setSelectedParker(p);
                                  setShowEditParkerModal(true);
                                },
                              },
                            ]
                          : []),
                        {
                          label: p.groupId
                            ? "Remove from Group"
                            : "Remove Parker",
                          icon: (
                            <Trash size={18} className={"text-muted mb-1"} />
                          ),
                          onClick: () => {
                            if (p.isDraft && p.draftId) {
                              if (p.groupId) {
                                setAllGuestParkers((prev) =>
                                  prev.map((x) =>
                                    x.draftId === p.draftId
                                      ? { ...x, groupId: null, groupName: null }
                                      : x
                                  )
                                );
                                return;
                              }

                              setAllGuestParkers((prev) =>
                                prev.filter((x) => x.draftId !== p.draftId)
                              );
                              return;
                            }

                            setRemoveParkerId(p.parkerId);
                            p.groupId
                              ? setRemoveParkerFromGroup(true)
                              : setShowRemove(true);
                          },
                        },
                      ]}
                    />
                  </div>

                  <div className="row g-2">
                    <div className="col-12">
                      <div className="bg-light rounded-3 py-2 px-2">
                        <div className="text-muted small">LICENSE PLATES</div>
                        <div className="fw-bold fs-6">
                          {getLpnText(p.vehicles)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {showChangeGroup && selectedParker && (
          <ChangeParkerGroupModal
            show={showChangeGroup}
            onClose={() => {
              setShowChangeGroup(false);
              setSelectedParker(null);
            }}
            parkerName={selectedParker.name}
            currentGroupId={selectedParker.groupId}
            groups={groups}
            onConfirm={(groupId) => {
              assignParkerToGroup(selectedParker, groupId);
            }}
          />
        )}
        {showEditParkerModal && selectedParker && (
          <ParkerEditModal
            show={showEditParkerModal}
            onClose={() => {
              setShowEditParkerModal(false);
              setSelectedParker(null);
            }}
            parker={selectedParker}
            setParker={setSelectedParker}
            parkers={allGuestParkers}
            setParkers={setAllGuestParkers}
            facilityId={subscription.facility.facilityId}
            selectedParkerSub={subscription}
          />
        )}

        <ConfirmModal
          title="Remove Parker From Group"
          message="Are you sure you want to remove this parker from the group?"
          show={removeParkerFromGroup}
          setShow={setRemoveParkerFromGroup}
          onConfirm={() => {
            const parker = allGuestParkers.find(
              (p) => !p.isDraft && p.parkerId === removeParkerId
            );
            if (parker) {
              assignParkerToGroup(parker, null);
            }
          }}
        />

        <AddVehicleModal
          show={showAddVehicle}
          setShow={setShowAddVehicle}
          onSubmit={vehicleAdd}
        />

        <ChooseVehicleModal
          show={showChooseVehicle}
          setShow={setShowChooseVehicle}
          onSubmit={vehicleLink}
          vehicles={chooseModalVehicles}
          exclude={chooseModalExclude}
          onClose={() => setShowChooseVehicle(false)}
        />

        <InviteParkerModal
          show={showAddParker}
          setShow={setShowAddParker}
          isFromGroupContext={true}
          submit={async (data: InviteParkerForm) => {
            const normalizedGroupId =
              data.subscriptionGroupId === UNASSIGNED_GROUP_ID
                ? null
                : data.subscriptionGroupId;
            parkerInvite(
              data,
              context === Context.FromPlanGroups ? normalizedGroupId! : groupId,
              undefined,
              false
            );
            setShowAddParker(false);
          }}
          groups={context === Context.FromPlanGroups ? groups : undefined}
        />

        <ConfirmModal
          title={"Remove Parker"}
          message={
            "Are you sure you want to remove this parker from your plan?"
          }
          show={showRemove}
          setShow={setShowRemove}
          onConfirm={() => {
            if (removeParkerId !== undefined) {
              parkerRemove(removeParkerId!);
            }
          }}
        />

        <ConfirmModal
          title="Discard changes?"
          message="If you cancel now, all unsaved changes will be lost."
          confirmText="Discard"
          show={showCancelConfirm}
          setShow={setShowCancelConfirm}
          onConfirm={cancelChanges}
        />

        <UnsavedChangesModal
          show={showModal}
          onStay={stay}
          onDiscard={discard}
        />
        <GroupModal
          show={showGroupModal}
          onClose={() => setShowGroupModal(false)}
          existingGroups={groups}
          onSubmit={async (name) => {
            await createGroup(name);
          }}
        />

        {showAddContract && (
          <ContractRuleModal
            show={showAddContract}
            onClose={() => setShowAddContract(false)}
            context={groupId ? { mode: "group", groupId } : { mode: "plan" }}
            onSubmit={async (data) => {
              await saveContract(data);
            }}
            isEditing={isEditingContract}
            groups={modalGroups}
            initialRules={groups.find((x) => x.id == groupId)?.contractRules}
            onRemoveContract={removeContract}
          />
        )}
      </div>
    </div>
  );
}
